import { storage } from "@/utils/utils";
import { LOCAL_STROAGE_GAME_CODE_KEY } from '@dbl/core/global';

/**
 * 1. 上新功能后, 当用户首次进入页面, 需要强制弹出弹窗, 提醒用户
 * 2. 只做用户隔离, 不做业务隔离, 也不做浏览器隔离
 * 3. 如果用户 手动点击了 关闭按钮, 那么以后将不再弹出; 如果用户不关闭, 那么下次进入还会弹出
 * 4. 设定一个过期时间, 超过这个时间, 提示弹窗将不再弹出
 */

// 'dbl-new-feature-bubble' 是Datalab系统所有新功能提示localStorage存储的key
const NewFeatureLocalKey = 'dbl_new_feature_bubble';

type FeatureBubbleConfigType = {
  localKey: string; // 存储在localStorage中的key
  expiryTime?: string; // 过期时间, 不配置, 意味着不过期
  formatMessageId: string; // 新功能提示文本的id(配置在_locales目录文件中的), 使用 dbl_chart_config_tip作为前缀
}

export const FeatureBubbleConfig: Record<string, FeatureBubbleConfigType> = {
  // 图表参考线新功能配置
  ReferenceLine: { // ReferenceLine 作为新功能id, 在调用方法时传入
    localKey: 'referenceLine',
    expiryTime: '2025-06-27',
    formatMessageId: 'dbl_chart_config_tip_reference_lines',
  }
  // 其他新功能配置
  
};

/**
 * 获取用户id
 */
const getUserId = () => {
  const {userId} = storage.getLocalStorageItem(LOCAL_STROAGE_GAME_CODE_KEY);
  return userId;
};

/**
 * 关闭新功能提示
 * @param {string} id 新功能id
 */
export const closeFeatureBubble = (id: string) => {
  const info = storage.getLocalStorageItem(NewFeatureLocalKey) || {};
  const feature = info[FeatureBubbleConfig[id].localKey] || [];
  
  const userId = getUserId();
  feature.push(userId);
  const newInfo = {...info, [FeatureBubbleConfig[id].localKey]: feature};
  
  storage.setLocalStorageItem(NewFeatureLocalKey, newInfo);
};


/**
 * 用户是否已经关闭新功能提示
 * @param {string} localKey 存储在localStorage中的key
 * @returns boolean 用户关闭了 返回true;
 */
export const isClosedFeatureBubble = (localKey: string): boolean => {
  const info = storage.getLocalStorageItem(NewFeatureLocalKey) || {};
  const userId = getUserId();
  // 如果本地localStorage中已经存在这个配置, 说明用户已经关闭了新功能提示.
  return !!info?.[localKey]?.includes(userId);
};

/**
 * 新功能提示时间是否已经过期
 * @param {string} id 新功能id
 * @returns {boolean} 过期返回true
 */
export const isFeatureBubbleExpired = (id: string): boolean => {
  if (!FeatureBubbleConfig[id]?.expiryTime) {
    // 没有配置, 意味着不过期
    return false;
  }
  return isTimeExpired(FeatureBubbleConfig[id].expiryTime);
};


/**
 * 检查当前时间是否超过指定时间
 * @param {Date|string} targetTime - 目标时间，可以是 Date 对象或可解析的时间字符串
 * @returns {boolean} - 如果当前时间超过目标时间返回 true，否则返回 false
 */
function isTimeExpired(targetTime: string): boolean {
  // 将输入转换为 Date 对象
  const targetDate = new Date(targetTime);
  
  // 检查时间是否有效
  if (isNaN(targetDate.getTime())) {
    throw new Error('Invalid time input');
  }
  
  // 获取当前时间
  const now = new Date();
  
  // 比较时间
  return now >= targetDate;
}

/**
 * 是否需要强制展示新功能弹窗
 * @param {string} id - 新功能id
 * @returns {boolean} - true 为 展示
 */
export const isShowFeatureBubble = (id: string): boolean => {
  if (!FeatureBubbleConfig[id]) {
    console.warn(id,'未配置新功能提示');
    return false;
  }
  const isExpired = isFeatureBubbleExpired(id);
  // 如果已过期, 无需展示, 返回false
  if (isExpired) return false;
  // 如果用户已关闭, 无需展示, 返回false
  const isClosed = isClosedFeatureBubble(FeatureBubbleConfig[id]?.localKey);
  if (isClosed) return false;
  // 其他情况需要展示
  return true;
};


